import type { RideState } from './model';

export const RETUNED_ARENAS = ['harbour-spin', 'canyon-playground'] as const;
export const localRulesVersion = (trackId: string) => RETUNED_ARENAS.some(id => id === trackId) ? 4 : 3;
export type ArenaStars = Record<string, Record<string, number>>;
export interface ArenaGoal {
  id: string; name: string; unit: string; targets: readonly [number, number, number];
  value(state: RideState): number;
}
export const trickVariety = (mask: number) => (mask & 127).toString(2).replace(/0/g, '').length;
const podium: ArenaGoal = { id: 'podium', name: 'Podium chaser', unit: 'place', targets: [3, 2, 1], value: s => s.position };
const GOALS: Record<string, ArenaGoal[]> = {
  'harbour-spin': [
    { id: 'gates', name: 'Line hunter', unit: 'gates', targets: [10, 15, 20], value: s => s.gates },
    { id: 'draft', name: 'Wheel surfer', unit: 'seconds drafting', targets: [6, 12, 20], value: s => s.draftSeconds }, podium,
  ],
  'canyon-playground': [
    { id: 'landings', name: 'Stick the landing', unit: 'trick landings', targets: [2, 4, 6], value: s => s.tricksLanded },
    { id: 'variety', name: 'Mix it up', unit: 'different tricks', targets: [2, 4, 6], value: s => trickVariety(s.trickTypes) }, podium,
  ],
};
export const goalsFor = (trackId: string): readonly ArenaGoal[] => GOALS[trackId] ?? [];
export const starsFor = (goal: ArenaGoal, state: RideState) => state.finished ? goal.targets.filter(target => goal.id === 'podium' ? goal.value(state) <= target : goal.value(state) >= target).length : 0;
export const totalStars = (stars: ArenaStars, trackId: string) => goalsFor(trackId).reduce((sum, goal) => sum + (stars[trackId]?.[goal.id] ?? 0), 0);
export function goalTarget(goal: ArenaGoal, tier: number): string {
  const target = goal.targets[Math.min(2, tier)];
  return goal.id === 'podium' ? `${target === 1 ? 'Win the race' : `Finish top ${target}`}` : `${target} ${goal.unit}`;
}
/** Only successful, finished rides bank stars. Repeating a tier never inflates progress. */
export function bankArenaGoals(stars: ArenaStars, trackId: string, state: RideState): number {
  let gained = 0;
  for (const goal of goalsFor(trackId)) {
    const before = stars[trackId]?.[goal.id] ?? 0, after = Math.max(before, starsFor(goal, state));
    stars[trackId] ??= {}; stars[trackId][goal.id] = after; gained += after - before;
  }
  return gained;
}
export function cleanArenaStars(value: unknown): ArenaStars {
  const clean: ArenaStars = {};
  if (!value || typeof value !== 'object') return clean;
  for (const trackId of RETUNED_ARENAS) {
    const saved = (value as ArenaStars)[trackId];
    if (!saved || typeof saved !== 'object') continue;
    clean[trackId] = Object.fromEntries(goalsFor(trackId).map(goal => [goal.id, Number.isInteger(saved[goal.id]) ? Math.max(0, Math.min(3, saved[goal.id])) : 0]));
  }
  return clean;
}
